import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Page from 'components/Page';
import { Alert, Badge, Button, Divider, Space, Tooltip } from 'antd';
import useDataSource from 'hooks/useDataSource';
import { EDIT_DATA_SOURCE, EDIT_LICENSE, EDIT_USERS } from 'shared/constants/permissions';
import { ExclamationCircleOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons';
import { FormattedDate, FormattedMessage, FormattedRelativeTime } from 'react-intl';
import useCustomer from 'hooks/useCustomer';
import globalMessages from 'translations/messages';
import { DataSourceType } from 'shared/services/prisma/types';
import LicenseStatus from 'components/LicenseStatus';
import Loading from 'components/Loading';
import NoMatch from 'containers/NoMatch';
import dayjs from 'dayjs';
import selectUnit from 'shared/utils/selectUnit';
import theme from 'shared/constants/theme.json';
import { isLicenseTrial } from 'shared/utils/license';
import Website from './Website/WebsiteDashboard';
import CX from './CX/CXDashboard';
import ChangeLicense from './License/ChangeLicense';

const extraFields = `
  isDemo
  access {
    id
  }
  website {
    id
    lastActivityTimestamp
    lastActivityUrl
  }
`;

const DataSourceDashboard = () => {
  const { hasCustomerPermission } = useCustomer();
  const { dataSource, loading, hasDataSourcePermission, refetch } = useDataSource({ extraFields, includeLicenseUsage: true });
  const [showChangeLicense, setShowChangeLicense] = useState(false);

  if (loading) return <Loading />;

  if (!dataSource) return <NoMatch />;

  const { website } = dataSource;

  const notCollecting = website && (!website?.lastActivityTimestamp || dayjs(website.lastActivityTimestamp).isBefore(dayjs().subtract(1, 'day')));

  const isTrial = isLicenseTrial(dataSource.activeLicense);

  return (
    <Page>
      <Page.Header
        title={dataSource.title}
        subTitle={(
          <Space split={<Divider type="vertical" />}>
            {website && (
              <Space>
                <Tooltip title={website.lastActivityTimestamp && (
                  <>
                    {`Last activity received from url ${website.lastActivityUrl} at `}
                    <FormattedDate value={website.lastActivityTimestamp} year="numeric" day="numeric" month="short" hour="numeric" minute="numeric" />
                  </>
                )}
                >
                  {notCollecting ? (
                    <>
                      <ExclamationCircleOutlined style={{ color: theme['error-color'], marginRight: 8 }} />
                      <span style={{ color: theme['error-color'] }}>
                        {website.lastActivityTimestamp ? (
                          <>
                            No data since
                            {' '}
                            <FormattedRelativeTime {...selectUnit(new Date(website.lastActivityTimestamp))} />
                          </>
                        ) : <>No data received</>}
                      </span>
                    </>
                  ) : (
                    <Badge status="success" text="Receiving data" />
                  )}
                </Tooltip>
              </Space>
            )}
            <Space>
              <LicenseStatus license={dataSource.activeLicense} currentUsage={dataSource.currentUsage} />
              {hasDataSourcePermission(EDIT_LICENSE) && (
                <Link to={`/data-sources/${dataSource.id}/settings/license`}>
                  <Button type="link" icon={<SettingOutlined />}>Manage license</Button>
                </Link>
              )}
            </Space>

            <Space>
              <Space>
                <TeamOutlined />
                <FormattedMessage {...globalMessages.userCount} values={{ count: dataSource.access.length }} />
              </Space>
              {hasCustomerPermission(EDIT_USERS) && hasCustomerPermission(EDIT_DATA_SOURCE) && (
                <Link to="/account/users">
                  <Button type="link" icon={<SettingOutlined />}>Manage users</Button>
                </Link>
              )}
            </Space>
          </Space>
        )}
      />
      {dataSource.isDemo && (
        <Alert
          message="You are currently viewing our demo account, where you can explore the features of Extellio. Start your own free trial to collect and see your own data."
          type="info"
          showIcon
        />
      )}
      {isTrial && hasDataSourcePermission(EDIT_LICENSE) && (
        <Alert
          message="You are currently on a trial license. Upgrade your license before the trial ends to avoid interruption."
          type="warning"
          showIcon
          action={<Button type="primary" onClick={() => setShowChangeLicense(true)}>Upgrade now</Button>}
        />
      )}
      {dataSource?.type === DataSourceType.Website && (
        <Website dataSourceId={dataSource?.id} notCollecting={notCollecting && hasDataSourcePermission(EDIT_DATA_SOURCE)} />
      )}
      {dataSource?.type === DataSourceType.Cx && (
        <CX />
      )}
      <ChangeLicense open={showChangeLicense} onCancel={() => setShowChangeLicense(false)} onChange={refetch} />
    </Page>

  );
};

export default DataSourceDashboard;
